/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { Box, Button, Typography } from '@mui/material';
import { parseAnswer, VERIFY_STATUS } from './mavenParse';
import VerifyBadge from './VerifyBadge';
import AnswerChart from './AnswerChart';
import AnswerTable from './AnswerTable';
import AnswerProof from './AnswerProof';
import { AIADMIN_FONT_FAMILY, AIADMIN_TEXT_PRIMARY } from '../../common/styles/aiadminThemeTokens';

/**
 * One answer: verify badge, text, chart, table and proof ids. The SQL blocks are taken out of the text and
 * not shown; verify still reads them from the full answer.
 * While `streaming`, the text grows as it arrives; the badge and proof ids wait for the end.
 * An answer the check says did not come from the data is hidden behind "Show anyway", so made-up numbers
 * never look believable.
 */
const MavenAnswer = ({ answer, check, streaming }) => {
  const parts = useMemo(() => parseAnswer(answer, { partial: streaming }), [answer, streaming]);
  const html = useMemo(() => DOMPurify.sanitize(marked.parse(parts.text || '')), [parts.text]);
  const [showAnyway, setShowAnyway] = useState(false);
  const hidden = !streaming && check?.result?.status === VERIFY_STATUS.NOT_VERIFIED && !showAnyway;

  if (hidden) {
    return (
      <Box>
        <VerifyBadge check={check} />
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.5 }}>
          <Typography variant="body2" sx={{ color: AIADMIN_TEXT_PRIMARY, fontFamily: AIADMIN_FONT_FAMILY }}>
            I couldn&apos;t check this answer against the data, so I&apos;ve hidden it. Please ask again.
          </Typography>
          <Button size="small" onClick={() => setShowAnyway(true)} sx={{ minWidth: 0, px: 0.5, textTransform: 'none' }}>
            Show anyway
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {!streaming && <VerifyBadge check={check} />}
      <Box
        sx={{
          mt: 1,
          fontSize: 14,
          '& p': { my: 0.75 },
          '& ul': { pl: 2.5, my: 0.75 },
          '& code': { fontSize: 12 },
          // The answer is plain HTML from Markdown (no MUI classes), so page-wide styles such as Studio's
          // global `p` font and colour would apply to it. Pin them here so it looks the same on every page.
          fontFamily: AIADMIN_FONT_FAMILY,
          color: AIADMIN_TEXT_PRIMARY,
          lineHeight: 1.5,
          '& p, & li, & ul, & ol, & strong, & em, & h1, & h2, & h3, & h4': {
            fontFamily: 'inherit',
            color: 'inherit',
            fontSize: 'inherit',
            lineHeight: 'inherit'
          }
        }}
        // The text comes from an AI, so it is always cleaned with DOMPurify first.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {parts.chart && <AnswerChart chart={parts.chart} />}
      {parts.table && <AnswerTable table={parts.table} />}
      {!streaming && <AnswerProof requestIds={parts.requestIds} />}
    </Box>
  );
};

MavenAnswer.propTypes = {
  answer: PropTypes.string.isRequired,
  check: VerifyBadge.propTypes.check,
  streaming: PropTypes.bool
};

MavenAnswer.defaultProps = { check: null, streaming: false };

export default MavenAnswer;
